export type SlotClass = string;

export interface CardStyleDriver {
  root: SlotClass;
  header: SlotClass;
  title: SlotClass;
  description: SlotClass;
  action: SlotClass;
  content: SlotClass;
  footer: SlotClass;
}

export const cardStyleDrivers: CardStyleDriver = {
  // Card root — glass + interactive hover lift + shine sweep
  root: "glass-card card-interactive card-shine rounded-theme-xl text-card-foreground flex flex-col overflow-auto py-theme-4 px-theme-5",
  // Header section — subtle separator (50% border opacity)
  header:
    "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start has-data-[slot=card-action]:grid-cols-[1fr_auto] ",
  // Title inside CardHeader
  title: "text-body leading-none font-semibold text-foreground",
  // Description under Title
  description: "text-muted-foreground text-sm mt-theme-1",
  // Optional action area in CardHeader
  action: "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
  // Main body/content area
  content: "",
  // Footer row — subtle separator matching header
  footer: "flex items-center px-theme-5 py-theme-4 border-t border-border/50",
};

export interface LabelStyleDriver {
  root: SlotClass;
}

export const labelStyleDriver: LabelStyleDriver = {
  root: "flex items-center gap-2 text-sm leading-none font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
};

export interface PageHeaderStyleDriver {
  root: SlotClass;
  left: SlotClass;
  title: SlotClass;
  right: SlotClass;
  iconButton: SlotClass;
}

export const pageHeaderStyleDriver: PageHeaderStyleDriver = {
  root:
    "flex items-center justify-between gap-theme-5 px-theme-6 py-theme-5 border-b-theme bg-background/80 backdrop-blur supports-backdrop-filter:bg-background/60",
  left: "flex items-center gap-theme-5",
  title:
    "tracking-tight text-foreground text-body font-semibold text-muted-foreground",
  right: "flex items-center gap-theme-3",
  iconButton: "h-8 w-8 p-0",
};

export interface SidebarStyleDriver {
  root: SlotClass;
  panel: SlotClass;
  headerRow: SlotClass;
  headerTitle: SlotClass;
  toggleButton: SlotClass;
  searchContainer: SlotClass;
  searchWrapper: SlotClass;
  searchIcon: SlotClass;
  searchInput: SlotClass;
  topSlotContainer: SlotClass;
  nav: SlotClass;
  section: SlotClass;
  sectionTitleRow: SlotClass;
  sectionTitleText: SlotClass;
  sectionTitleInteractive: SlotClass;
  expandIconWrapper: SlotClass;
  itemsList: SlotClass;
  itemRow: SlotClass;
  itemRowDisabled: SlotClass;
  itemIcon: SlotClass;
  itemLabel: SlotClass;
  itemBadgeWrapper: SlotClass;
  bottomSlotWrapper: SlotClass;
  footerWrapper: SlotClass;
  // Collapsed mode styles
  collapsedTopbar: SlotClass;
  collapsedTopbarContent: SlotClass;
  popupOverlay: SlotClass;
  popupContainer: SlotClass;
}

export const sidebarStyleDriver: SidebarStyleDriver = {
  root: "h-full shrink-0 overflow-auto p-theme-5 bg-sidebar border-border dark:border-border-dark",
  panel: "flex h-full flex-col overflow-auto grid grid-rows-[auto_1fr_auto] gap-theme-3",
  headerRow: "flex items-center",
  headerTitle: "flex-1",
  toggleButton: "h-8 w-8",
  searchContainer: "px-theme-3 pb-theme-3",
  searchWrapper: "relative",
  searchIcon: "absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground",
  searchInput: "pl-9",
  topSlotContainer: "px-theme-3",
  nav: "overflow-auto scrollbar-hidden",
  section: "mb-theme-3 rounded-theme-lg p-theme-2",
  sectionTitleRow:
    "px-theme-3 py-theme-2 flex items-center justify-between text-caption text-muted-foreground",
  sectionTitleText: "text-sm font-semibold",
  sectionTitleInteractive:
    "cursor-pointer hover:text-foreground hover:bg-accent/50 rounded-theme-md transition-colors select-none",
  expandIconWrapper: "transition-all duration-200 h-4 w-4",
  itemsList: "space-y-1 py-theme-3",
  itemRow:
    "group flex items-center rounded-theme-lg text-foreground/80 px-theme-3 py-theme-2 text-sm hover:bg-accent transition-colors border-2 border-transparent ring-[1.5px] ring-transparent",
  itemRowDisabled: "opacity-60 pointer-events-none",
  itemIcon: "shrink-0 [&_svg]:size-5",
  itemLabel: "flex-1 truncate font-semibold pl-2",
  itemBadgeWrapper: "text-xs",
  bottomSlotWrapper: "",
  footerWrapper: "",
  // Collapsed mode styles
  collapsedTopbar: "h-14 flex items-center px-theme-5 gap-theme-3 border-b-theme bg-sidebar overflow-hidden",
  collapsedTopbarContent: "flex items-center gap-theme-3 animate-topbar-in",
  popupOverlay: "absolute left-0 top-14 z-50 h-[calc(100%-3.5rem)]",
  popupContainer: "h-full bg-background border-r-theme shadow-lg animate-sidebar-in",
};

export interface TabsStyleDriver {
  root: SlotClass;
  list: SlotClass;
  trigger: SlotClass;
  content: SlotClass;
}

export const tabsStyleDriver: TabsStyleDriver = {
  root: "flex flex-col gap-theme-3 py-theme-2",
  list: "inline-flex items-center gap-theme-2 rounded-theme-lg bg-background p-theme-2 ring-0",
  trigger:
    "inline-flex items-center justify-center rounded-theme-full px-theme-5 py-theme-3 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:font-bold data-[disabled]:opacity-50 data-[disabled]:pointer-events-none",
  content: "flex-1 outline-none border-t-theme",
};

export interface ButtonStyleDriver {
  root: SlotClass;
  variants: Record<
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link",
    string
  >;
  sizes: Record<"default" | "sm" | "lg" | "icon", string>;
}

export const buttonStyleDriver: ButtonStyleDriver = {
  root:
    "inline-flex items-center rounded-theme-md justify-center gap-theme-3 whitespace-nowrap text-sm font-semibold transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive cursor-pointer active:scale-[0.97]",
  variants: {
    default: "bg-primary text-primary-foreground shadow-theme-sm hover:bg-primary/90",
    destructive:
      "text-white shadow-theme-sm border-theme text-destructive focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
    outline:
      "border border-primary text-primary bg-transparent shadow-theme-sm hover:bg-primary/10 dark:border-primary/30 dark:bg-primary/10 dark:hover:bg-primary/15",
    secondary:
      "bg-secondary text-secondary-foreground shadow-theme-sm hover:bg-secondary/80",
    ghost:
      "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
    link: "text-primary underline-offset-4 hover:underline",
  },
  sizes: {
    default: "h-9 px-theme-5 py-theme-3 has-[>svg]:px-theme-3",
    sm: "h-8 gap-theme-2 px-theme-3 has-[>svg]:px-theme-2",
    lg: "h-10 px-theme-6 has-[>svg]:px-theme-5",
    icon: "size-9",
  },
};

export interface InputStyleDriver {
  root: SlotClass;
}

export const inputStyleDriver: InputStyleDriver = {
  root:
    "file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex h-9 w-full min-w-0 rounded-theme-md border bg-background px-theme-3 py-theme-2 text-sm transition-[color,box-shadow] outline-none file:inline-flex file:h-8 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
};

export interface SelectStyleDriver {
  root: SlotClass;
  label: SlotClass;
  control: SlotClass;
  valueContainer: SlotClass;
  placeholder: SlotClass;
  singleValue: SlotClass;
  multiValue: SlotClass;
  multiValueLabel: SlotClass;
  multiValueRemove: SlotClass;
  indicators: SlotClass;
  menu: SlotClass;
  option: SlotClass;
  optionFocused: SlotClass;
  optionSelected: SlotClass;
  helpText: SlotClass;
  errorText: SlotClass;
}

export const selectStyleDriver: SelectStyleDriver = {
  root: "flex flex-col gap-theme-2",
  label:
    "leading-none font-medium text-foreground peer-disabled:cursor-not-allowed peer-disabled:opacity-50 text-body",
  control:
    "border-input rounded-theme-md bg-background h-9 px-theme-3 py-theme-2 ring-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  valueContainer: "gap-theme-2 px-theme-3 py-theme-2",
  placeholder: "text-muted-foreground text-sm",
  singleValue: "text-sm",
  multiValue:
    "inline-flex items-center gap-theme-2 rounded-theme-lg bg-accent text-accent-foreground px-theme-2 py-theme-2 text-xs",
  multiValueLabel: "text-xs",
  multiValueRemove:
    "rounded-theme-sm hover:bg-destructive/10 hover:text-destructive text-xs",
  indicators: "px-theme-2 gap-theme-2",
  menu: "z-[60] bg-popover border-theme rounded-theme-md shadow-theme overflow-hidden",
  option:
    "cursor-pointer px-theme-3 py-theme-2 text-sm hover:bg-accent hover:text-accent-foreground",
  optionFocused: "bg-accent text-accent-foreground",
  optionSelected:
    "bg-primary/10 text-foreground font-semibold hover:bg-primary/20",
  helpText: "text-muted-foreground",
  errorText: "text-destructive text-xs",
};

export interface AlertStyleDriver {
  root: SlotClass;
  variants: Record<"default" | "destructive" | "success" | "warning", string>;
}

export const alertStyleDriver: AlertStyleDriver = {
  root:
    "relative w-full rounded-theme-lg px-theme-5 py-theme-3 gap-theme-3 text-sm flex items-center",
  variants: {
    default:
      "border border-blue-500/30 bg-blue-500/10 text-blue-700 dark:text-blue-400 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-400",
    destructive:
      "border border-destructive/40 bg-destructive/10 text-destructive bg-red-50 border-red-200",
    success:
      "border border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400 [&>svg]:text-green-600 dark:[&>svg]:text-green-400",
    warning:
      "border border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400",
  },
};

export interface BadgeStyleDriver {
  root: SlotClass;
  variants: Record<"default" | "secondary" | "destructive" | "outline" | "success" | "warning" | "error", string>;
}

export const badgeStyleDriver: BadgeStyleDriver = {
  root:
    "inline-flex items-center gap-theme-2 whitespace-nowrap rounded-theme-full border px-theme-3 py-theme-2 text-xs font-semibold transition-colors shadow-theme-sm [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3",
  variants: {
    default: "bg-primary text-primary-foreground border-transparent",
    secondary: "bg-secondary text-secondary-foreground border-transparent",
    destructive:
      "bg-destructive text-white border-transparent dark:bg-destructive/60",
    outline:
      "bg-background text-foreground border-theme dark:bg-input/30",
    success:
      "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800",
    warning:
      "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800",
    error:
      "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-800",
  },
};

export interface AccordionStyleDriver {
  root: SlotClass;
  item: SlotClass;
  triggerHeader: SlotClass;
  trigger: SlotClass;
  triggerIcon: SlotClass;
  content: SlotClass;
  contentInner: SlotClass;
}

export const accordionStyleDriver: AccordionStyleDriver = {
  root: "w-full",
  item: "border-b-theme last:border-b-0",
  triggerHeader: "flex",
  trigger:
    "focus-visible:border-ring focus-visible:ring-ring/50 flex flex-1 items-start justify-between gap-theme-5 rounded-theme-md py-theme-5 text-left text-sm font-semibold transition-all outline-none hover:underline focus-visible:ring-[3px] disabled:pointer-events-none disabled:opacity-50 [&[data-state=open]>svg]:rotate-180",
  triggerIcon:
    "text-muted-foreground pointer-events-none size-4 shrink-0 translate-y-0.5 transition-transform duration-200",
  content:
    "data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden text-sm",
  contentInner: "pt-0 pb-theme-5",
};

export interface SheetStyleDriver {
  overlay: SlotClass;
  contentBase: SlotClass;
  contentSides: {
    top: SlotClass;
    right: SlotClass;
    bottom: SlotClass;
    left: SlotClass;
  };
  content: SlotClass;
  header: SlotClass;
  footer: SlotClass;
  title: SlotClass;
  description: SlotClass;
  close: SlotClass;
}

export const sheetStyleDriver: SheetStyleDriver = {
  overlay:
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
  contentBase:
    "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 flex flex-col gap-theme-5 shadow-theme-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500",
  content: "h-full overflow-auto grid gap-0 grid-rows-[auto_1fr_auto]",
  contentSides: {
    right:
      "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-3/4 border-l-theme sm:max-w-sm",
    left:
      "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 left-0 h-full w-3/4 border-r-theme sm:max-w-sm",
    top:
      "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 h-auto border-b-theme",
    bottom:
      "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 h-auto border-t-theme",
  },
  header: "flex flex-col gap-theme-2 p-theme-5 border-b-theme",
  footer: "mt-auto flex flex-row items-center justify-center gap-theme-3 p-theme-5 border-t-theme",
  title: "text-foreground font-semibold",
  description: "text-muted-foreground text-sm",
  close:
    "ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute top-4 right-4 rounded-theme-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none",
};

export interface ModalStyleDriver {
  overlay: SlotClass;
  content: SlotClass;
  headerRow: SlotClass;
  headerTitle: SlotClass;
  headerActions: SlotClass;
  body: SlotClass;
  footer: SlotClass;
  close: SlotClass;
}

export const modalStyleDriver: ModalStyleDriver = {
  overlay:
    "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50",
  content:
    "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-1/2 left-1/2 z-50 grid w-full -translate-x-1/2 -translate-y-1/2 gap-0 rounded-theme-xl border-theme shadow-theme-lg duration-200 w-1/2",
  headerRow:
    "flex items-center justify-between gap-theme-3 border-b-theme px-theme-6 py-theme-3",
  headerTitle: "text-body leading-none font-semibold",
  headerActions: "flex items-center gap-theme-3",
  body: "px-theme-6 py-theme-5",
  footer: "flex items-center justify-center gap-theme-3 border-t-theme px-theme-6 py-theme-3",
  close:
    "ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-theme-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
};

export interface HorizontalTabsStyleDriver {
  root: SlotClass;
  list: SlotClass;
  trigger: SlotClass;
  content: SlotClass;
}

export const horizontalTabsStyleDriver: HorizontalTabsStyleDriver = {
  root: "w-full gap-0 py-0 grid grid-rows-[auto_1fr] h-full overflow-auto",
  list: "ring-0 border-none bg-background flex gap-theme-2 px-theme-6 py-theme-3",
  trigger:
    "data-[state=active]:bg-primary-lighter data-[state=active]:text-primary data-[state=active]:ring-1 data-[state=active]:ring-primary border-2 border-primary-foreground text-body font-semibold data-[state=active]:font-bold py-theme-2 rounded-theme-lg px-theme-3",
  content: "h-full overflow-auto",
};

export interface VerticalTabsStyleDriver {
  root: SlotClass;
  list: SlotClass;
  trigger: SlotClass;
  labelVertical: SlotClass;
  labelHorizontal: SlotClass;
  contentContainer: SlotClass;
  content: SlotClass;
}

export const verticalTabsStyleDriver: VerticalTabsStyleDriver = {
  root: "w-full grid grid-cols-[auto_1fr] divide-x gap-0 p-0",
  list: "h-auto flex flex-col py-theme-5",
  trigger:
    "px-theme-2 py-theme-3 justify-center after:hidden data-[state=active]:bg-primary data-[state=active]:text-primary-foreground text-sm data-[state=active]:font-bold font-semibold rounded-theme-lg whitespace-nowrap",
  labelVertical: "[writing-mode:vertical-rl] rotate-180 [text-orientation:mixed]",
  labelHorizontal: "",
  contentContainer: "flex-1 px-theme-5 py-theme-3",
  content: "p-0 border-0",
};

export interface KanbanStyleDriver {
  board: SlotClass;
  columnRoot: SlotClass;
  columnHandle: SlotClass;
  itemRoot: SlotClass;
  itemHandle: SlotClass;
}

export const kanbanStyleDriver: KanbanStyleDriver = {
  // Kanban board container
  board: "flex size-full divide-x",

  // Column container
  columnRoot:
    "flex size-full flex-col gap-theme-3 bg-card px-theme-5 py-theme-3 text-card-foreground aria-disabled:pointer-events-none aria-disabled:opacity-50",

  // Draggable handle for the column header
  columnHandle:
    "select-none text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50",

  // Kanban card (item) container
  itemRoot:
    "rounded-theme-lg bg-background focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",

  // Draggable handle for the kanban card (item)
  itemHandle:
    "select-none text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50",
};
export interface ListingStyleDriver {
  root: SlotClass;
  header: SlotClass;
  filters: SlotClass;
  bodyContainer: SlotClass;
  tableHeader: SlotClass;
  pageContainer: SlotClass;
  itemRow: SlotClass;
  itemIcon: SlotClass;
  itemTitle: SlotClass;
  itemMeta: SlotClass;
  itemColumns: SlotClass;
  itemActions: SlotClass;
  emptyState: SlotClass;
  paginationRow: SlotClass;
}

export const listingStyleDriver: ListingStyleDriver = {
  root: "h-full grid grid-rows-[auto_auto_1fr] overflow-auto",
  header: "",
  filters: "px-theme-6 py-theme-3",
  bodyContainer: "h-full overflow-auto",
  tableHeader: "bg-muted/40",
  pageContainer: "h-full overflow-auto",
  itemRow:
    "grid grid-cols-[auto_1fr_auto] items-center gap-theme-3 px-theme-6 py-theme-3 border-b-theme hover:bg-accent/30",
  itemIcon:
    "border-theme rounded-theme-full p-theme-2 bg-accent/60 group-hover:bg-background",
  itemTitle: "text-sm font-semibold leading-none",
  itemMeta: "text-xs text-muted-foreground",
  itemColumns: "",
  itemActions: "flex items-center justify-end gap-theme-3",
  emptyState: "h-full w-full flex items-center justify-center text-sm text-muted-foreground",
  paginationRow: "border-t-theme px-theme-3 py-theme-2",
};

export interface DropdownMenuStyleDriver {
  root: SlotClass;
  trigger: SlotClass;
  content: SlotClass;
  item: SlotClass;
  itemWithIcon: SlotClass;
  separator: SlotClass;
  label: SlotClass;
  shortcut: SlotClass;
  subTrigger: SlotClass;
  subContent: SlotClass;
  checkboxItem: SlotClass;
  radioItem: SlotClass;
}

export const dropdownMenuStyleDriver: DropdownMenuStyleDriver = {
  root: "",
  trigger: "inline-flex items-center justify-center gap-theme-3 rounded-theme-md text-body font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 px-theme-3",
  content: "z-50 min-w-[180px] overflow-hidden rounded-theme-lg border-theme bg-popover p-theme-2 text-popover-foreground shadow-theme data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  item: "relative flex cursor-pointer select-none items-center gap-theme-3 rounded-theme-md px-theme-3 py-theme-2 text-body outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  itemWithIcon: "relative flex cursor-pointer select-none items-center gap-theme-3 rounded-theme-md px-theme-3 py-theme-2 text-body outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  separator: "-mx-1.5 my-theme-2 h-px bg-border",
  label: "px-theme-3 py-theme-2 text-caption font-semibold text-muted-foreground",
  shortcut: "ml-auto text-caption tracking-widest text-muted-foreground",
  subTrigger: "flex cursor-pointer select-none items-center gap-theme-3 rounded-theme-md px-theme-3 py-theme-2 text-body outline-none focus:bg-accent hover:bg-accent data-[state=open]:bg-accent",
  subContent: "z-50 min-w-[180px] overflow-hidden rounded-theme-lg border-theme bg-popover p-theme-2 text-popover-foreground shadow-theme data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
  checkboxItem: "relative flex cursor-pointer select-none items-center gap-theme-3 rounded-theme-md py-theme-2 pl-8 pr-theme-3 text-body outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
  radioItem: "relative flex cursor-pointer select-none items-center gap-theme-3 rounded-theme-md py-theme-2 pl-8 pr-theme-3 text-body outline-none transition-colors focus:bg-accent focus:text-accent-foreground hover:bg-accent data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
};
export interface FilterBuilderStyleDriver {
  root: SlotClass;
  groupContainer: SlotClass;
  groupContainerOdd: SlotClass;
  groupContainerEven: SlotClass;
  groupHeader: SlotClass;
  groupControls: SlotClass;
  ruleRow: SlotClass;
  field: SlotClass;
  operator: SlotClass;
  value: SlotClass;
  toggles: SlotClass;
  actions: SlotClass;
  emptyState: SlotClass;
  jsonPreview: SlotClass;
}

export const filterBuilderStyleDriver: FilterBuilderStyleDriver = {
  root: "flex flex-col gap-theme-3",
  groupContainer: "rounded-theme-xl border-theme",
  groupContainerOdd: "bg-background",
  groupContainerEven: "bg-accent/40",
  groupHeader: "flex items-center justify-between px-theme-5 py-theme-3",
  groupControls: "flex items-center gap-theme-3",
  ruleRow: "grid grid-cols-[1fr_auto_1fr_auto] items-center gap-theme-3 px-theme-5 py-theme-3",
  field: "min-w-0",
  operator: "w-36",
  value: "min-w-0",
  toggles: "flex items-center gap-theme-3",
  actions: "flex items-center gap-theme-3",
  emptyState: "px-theme-7 py-theme-6 text-center text-sm text-muted-foreground",
  jsonPreview: "bg-muted p-theme-3 rounded-theme-lg text-xs overflow-auto max-h-60",
};

export interface ProgressStyleDriver {
  track: SlotClass;
  indicator: SlotClass;
}

export const progressStyleDriver: ProgressStyleDriver = {
  track: "relative w-full overflow-hidden rounded-theme-full bg-secondary dark:bg-secondary/40",
  indicator:
    "absolute left-0 top-0 h-full w-full translate-x-[-100%] transform-gpu transition-transform duration-300 ease-linear flex items-center px-theme-3 text-primary-foreground bg-primary",
};

export interface TooltipStyleDriver {
  content: SlotClass;
}

export const tooltipStyleDriver: TooltipStyleDriver = {
  content:
    "bg-popover border-theme text-popover-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-theme-md px-theme-3 py-theme-2 text-xs text-balance",
};

export interface ScaffoldStyleDriver {
  root: SlotClass;
  background: SlotClass;
}

export const scaffoldStyleDriver: ScaffoldStyleDriver = {
  root: "relative min-h-screen w-full bg-background text-foreground antialiased",
  background: "absolute inset-0 -z-10",
};

export interface PageContainerStyleDriver {
  root: SlotClass;
  sizes: Record<"sm" | "md" | "lg" | "xl" | "full", string>;
}

export const pageContainerStyleDriver: PageContainerStyleDriver = {
  root: "mx-auto w-full px-theme-8 py-theme-6",
  sizes: {
    sm: "max-w-screen-sm",
    md: "max-w-screen-md",
    lg: "max-w-screen-lg",
    xl: "max-w-screen-xl",
    full: "max-w-full",
  },
};

export interface SectionStyleDriver {
  root: SlotClass;
  header: SlotClass;
  title: SlotClass;
  description: SlotClass;
  content: SlotClass;
  divider: SlotClass;
}

export const sectionStyleDriver: SectionStyleDriver = {
  root: "py-theme-5 first:pt-0 last:pb-0",
  header: "mb-theme-6",
  title: "text-h4 font-semibold text-foreground",
  description: "mt-theme-2 text-body text-muted-foreground",
  content: "",
  divider: "border-t-theme mt-theme-7",
};

export interface AvatarStyleDriver {
  root: SlotClass;
  image: SlotClass;
  fallback: SlotClass;
}

export const avatarStyleDriver: AvatarStyleDriver = {
  root: "relative flex size-8 shrink-0 overflow-hidden rounded-theme-full",
  image: "aspect-square size-full",
  fallback:
    "bg-muted flex size-full items-center justify-center rounded-theme-full text-sm font-medium",
};

export interface CalendarStyleDriver {
  root: SlotClass;
  months: SlotClass;
  month: SlotClass;
  monthCaption: SlotClass;
  caption: SlotClass;
  captionLabel: SlotClass;
  nav: SlotClass;
  navButton: SlotClass;
  table: SlotClass;
  headRow: SlotClass;
  headCell: SlotClass;
  row: SlotClass;
  cell: SlotClass;
  day: SlotClass;
  daySelected: SlotClass;
  dayToday: SlotClass;
  dayOutside: SlotClass;
  dayDisabled: SlotClass;
  dayRangeMiddle: SlotClass;
  dayHidden: SlotClass;
  rangeStart: SlotClass;
  rangeEnd: SlotClass;
  selected: SlotClass;
  weekdays: SlotClass;
}

export const calendarStyleDriver: CalendarStyleDriver = {
  root: "py-theme-2 font-semibold text-muted-foreground",
  months: "flex flex-row gap-theme-3",
  month: "text-body",
  monthCaption: "text-body font-bold py-theme-3",
  caption: "flex justify-center relative items-center font-bold",
  captionLabel: "text-sm font-medium px-theme-3 text-foreground",
  nav: "absolute right-4 top-12 gap-theme-2 flex",
  navButton:
    "h-7 w-7 bg-transparent opacity-50 hover:opacity-100 rounded-theme-md border-theme",
  table: "w-full border-collapse space-y-1",
  headRow: "flex",
  headCell: "text-muted-foreground rounded-theme-md w-9 font-normal text-[0.8rem]",
  row: "flex w-full mt-theme-3",
  cell: "h-8 w-8 text-center text-sm p-0 relative",
  day: "h-8 w-8 text-center p-0 aria-selected:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-theme-md",
  daySelected:
    "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
  dayToday: "bg-primary-lighter text-primary",
  dayOutside:
    "text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
  dayDisabled: "text-muted-foreground opacity-50",
  dayRangeMiddle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
  dayHidden: "invisible",
  rangeStart: "!bg-primary text-primary-foreground rounded-l-lg",
  rangeEnd: "!bg-primary text-primary-foreground rounded-r-lg",
  selected: "text-primary rounded-none font-bold bg-primary-lighter",
  weekdays: "h-9",
};

export interface PopoverStyleDriver {
  content: SlotClass;
}

export const popoverStyleDriver: PopoverStyleDriver = {
  content:
    "z-50 w-72 rounded-theme-lg border-theme bg-popover p-theme-3 text-popover-foreground shadow-theme outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2",
};

export interface SelectUIStyleDriver {
  trigger: SlotClass;
  content: SlotClass;
  viewport: SlotClass;
  label: SlotClass;
  item: SlotClass;
  separator: SlotClass;
  scrollButton: SlotClass;
}

export const selectUIStyleDriver: SelectUIStyleDriver = {
  trigger:
    "border-input data-[placeholder]:text-muted-foreground [&_svg:not([class*='text-'])]:text-muted-foreground focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:bg-input/30 dark:hover:bg-input/50 flex w-fit items-center justify-between gap-theme-3 rounded-theme-md border bg-transparent px-theme-3 py-theme-2 text-sm whitespace-nowrap shadow-theme-sm transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  content:
    "bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-(--radix-select-content-available-height) min-w-[8rem] origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-theme-md border-theme shadow-theme",
  viewport: "p-theme-2",
  label: "text-muted-foreground px-theme-3 py-theme-2 text-xs",
  item: "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-theme-3 rounded-theme-sm py-theme-2 pr-8 pl-theme-3 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
  separator: "bg-border pointer-events-none -mx-1 my-theme-2 h-px",
  scrollButton: "flex cursor-default items-center justify-center py-theme-2",
};

export interface SkeletonStyleDriver {
  root: SlotClass;
}

export const skeletonStyleDriver: SkeletonStyleDriver = {
  root: "bg-accent animate-pulse rounded-theme-md",
};

export interface PaginationStyleDriver {
  root: SlotClass;
  container: SlotClass;
  leftSection: SlotClass;
  pageNumbers: SlotClass;
  pageNumber: SlotClass;
  pageNumberActive: SlotClass;
  pageNumberInactive: SlotClass;
  rightSection: SlotClass;
  prevButton: SlotClass;
  prevButtonDisabled: SlotClass;
  nextButton: SlotClass;
  nextButtonDisabled: SlotClass;
  pageSelect: SlotClass;
  pageSizeSelect: SlotClass;
  ellipsis: SlotClass;
}

export const paginationStyleDriver: PaginationStyleDriver = {
  root: "flex justify-between w-full gap-theme-3 items-center py-theme-2 px-theme-3",
  container: "flex w-full gap-theme-3 items-center",
  leftSection: "flex w-full gap-theme-3 items-center",
  pageNumbers: "flex w-full",
  pageNumber: "px-theme-3 rounded-theme-md min-w-[1.75rem] h-7 flex items-center justify-center cursor-pointer transition-all duration-300",
  pageNumberActive: "text-white bg-primary",
  pageNumberInactive:
    "text-muted-foreground bg-accent/50 hover:bg-primary/10 hover:text-foreground",
  rightSection: "flex gap-theme-3 items-center",
  prevButton:
    "flex items-center justify-center gap-theme-2 text-xl pl-theme-2 pr-theme-3 h-7 rounded-theme-md hover:bg-accent/50 transition-all duration-300 text-primary cursor-pointer",
  prevButtonDisabled: "text-secondary-200 cursor-default",
  nextButton:
    "h-7 flex items-center justify-center gap-theme-2 text-xl pl-theme-3 pr-theme-2 hover:bg-accent/50 transition-all duration-300 rounded-theme-md text-primary cursor-pointer",
  nextButtonDisabled: "text-secondary-200 cursor-default",
  pageSelect: "w-32",
  pageSizeSelect: "w-24",
  ellipsis: "px-theme-3 text-sm text-muted-foreground",
};

// Empty State Component
export interface EmptyStateStyleDriver {
  root: SlotClass;
  icon: SlotClass;
  title: SlotClass;
  description: SlotClass;
  actions: SlotClass;
}

export const emptyStateStyleDriver: EmptyStateStyleDriver = {
  root: "flex flex-col items-center justify-center py-theme-7 px-theme-5 text-center",
  icon: "mb-theme-5 text-muted-foreground [&>svg]:size-12 [&>svg]:stroke-[1.5]",
  title: "text-lg font-semibold text-foreground mb-theme-3",
  description: "text-sm text-muted-foreground max-w-sm mb-theme-6",
  actions: "flex items-center gap-theme-3",
};

// SearchBox Component
export interface SearchBoxStyleDriver {
  root: SlotClass;
  inputWrapper: SlotClass;
  icon: SlotClass;
  input: SlotClass;
  clearButton: SlotClass;
}

export const searchBoxStyleDriver: SearchBoxStyleDriver = {
  root: "relative w-full",
  inputWrapper: "relative flex items-center",
  icon: "absolute left-3 text-muted-foreground pointer-events-none [&>svg]:size-4",
  input: "pl-9 pr-8 w-full",
  clearButton: "absolute right-2 text-muted-foreground hover:text-foreground cursor-pointer transition-colors duration-fast [&>svg]:size-4",
};

// Spinner Component
export interface SpinnerStyleDriver {
  root: SlotClass;
  sizes: Record<"sm" | "md" | "lg" | "xl", string>;
}

export const spinnerStyleDriver: SpinnerStyleDriver = {
  root: "animate-spin rounded-full border-current border-t-transparent",
  sizes: {
    sm: "h-4 w-4 border-2",
    md: "h-6 w-6 border-2",
    lg: "h-8 w-8 border-[3px]",
    xl: "h-12 w-12 border-4",
  },
};

// Loading State Component
export interface LoadingStateStyleDriver {
  root: SlotClass;
  message: SlotClass;
  centeredContainer: SlotClass;
  overlay: SlotClass;
  fullScreen: SlotClass;
}

export const loadingStateStyleDriver: LoadingStateStyleDriver = {
  root: "flex items-center gap-theme-3",
  message: "text-sm text-muted-foreground",
  centeredContainer: "flex items-center justify-center min-h-48 w-full",
  overlay: "absolute inset-0 z-10 flex items-center justify-center bg-background/60 backdrop-blur-[2px]",
  fullScreen: "fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm",
};

// Error State Component
export interface ErrorStateStyleDriver {
  root: SlotClass;
  iconWrapper: SlotClass;
  title: SlotClass;
  description: SlotClass;
  actions: SlotClass;
}

export const errorStateStyleDriver: ErrorStateStyleDriver = {
  root: "flex flex-col items-center justify-center py-theme-7 px-theme-5 text-center",
  iconWrapper: "rounded-theme-full bg-destructive/10 p-theme-5 mb-theme-5",
  title: "text-lg font-semibold text-foreground mb-theme-3",
  description: "text-sm text-muted-foreground mb-theme-6 max-w-sm",
  actions: "flex items-center gap-theme-3",
};

// Form Field Component
export interface FormFieldStyleDriver {
  root: SlotClass;
  label: SlotClass;
  hint: SlotClass;
  error: SlotClass;
  requiredIndicator: SlotClass;
}

export const formFieldStyleDriver: FormFieldStyleDriver = {
  root: "space-y-2",
  label: "text-sm font-medium text-foreground",
  hint: "mt-theme-2 text-xs text-muted-foreground",
  error: "mt-theme-2 text-xs text-destructive",
  requiredIndicator: "text-destructive ml-1",
};

// Form Section Component
export interface FormSectionStyleDriver {
  root: SlotClass;
  header: SlotClass;
  title: SlotClass;
  description: SlotClass;
  content: SlotClass;
  actions: SlotClass;
}

export const formSectionStyleDriver: FormSectionStyleDriver = {
  root: "py-theme-6 first:pt-0 last:pb-0",
  header: "mb-theme-6",
  title: "text-base font-semibold text-foreground",
  description: "mt-theme-2 text-sm text-muted-foreground",
  content: "space-y-theme-4",
  actions: "flex items-center gap-theme-3 pt-theme-6 border-t-theme mt-theme-6",
};

// Checkbox Component
export interface CheckboxStyleDriver {
  root: SlotClass;
  indicator: SlotClass;
  label: SlotClass;
  description: SlotClass;
  error: SlotClass;
}

export const checkboxStyleDriver: CheckboxStyleDriver = {
  root: "peer h-4 w-4 shrink-0 rounded-theme-sm border border-primary shadow-theme-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground",
  indicator: "flex items-center justify-center text-current",
  label: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
  description: "text-xs text-muted-foreground",
  error: "text-xs text-destructive",
};

// Radio Group Component
export interface RadioGroupStyleDriver {
  root: SlotClass;
  item: SlotClass;
  indicator: SlotClass;
  label: SlotClass;
  description: SlotClass;
  card: SlotClass;
  cardSelected: SlotClass;
}

export const radioGroupStyleDriver: RadioGroupStyleDriver = {
  root: "grid gap-3",
  item: "aspect-square h-4 w-4 rounded-full border border-primary shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  indicator: "flex items-center justify-center",
  label: "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
  description: "text-xs text-muted-foreground",
  card: "relative flex items-start gap-4 rounded-lg border p-4 cursor-pointer hover:border-primary/50 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  cardSelected: "border-primary bg-primary/5",
};

export interface AgGridStyleDriver {
  container: SlotClass;
}

export const agGridStyleDriver: AgGridStyleDriver = {
  container: "w-full h-full ag-theme-alpine",
};

// DataTable Component
export interface DataTableStyleDriver {
  root: SlotClass;
  toolbar: SlotClass;
  toolbarLeft: SlotClass;
  toolbarRight: SlotClass;
  filterInput: SlotClass;
  tableContainer: SlotClass;
  table: SlotClass;
  thead: SlotClass;
  headerRow: SlotClass;
  headerCell: SlotClass;
  headerCellPinned: SlotClass;
  headerCellGroup: SlotClass;
  headerCellSortable: SlotClass;
  headerCellContent: SlotClass;
  filterButton: SlotClass;
  filterButtonActive: SlotClass;
  tbody: SlotClass;
  bodyRow: SlotClass;
  bodyRowSelected: SlotClass;
  bodyCell: SlotClass;
  bodyCellPinned: SlotClass;
  expandButton: SlotClass;
  emptyState: SlotClass;
  footer: SlotClass;
  footerText: SlotClass;
  // Filter popover styles
  filterPopoverHeader: SlotClass;
  filterPopoverTitle: SlotClass;
  filterPopoverSearchInput: SlotClass;
  filterPopoverSelectAll: SlotClass;
  filterPopoverSelectAllLabel: SlotClass;
  filterPopoverOptionsList: SlotClass;
  filterPopoverOption: SlotClass;
  filterPopoverOptionLabel: SlotClass;
  filterPopoverEmpty: SlotClass;
  filterPopoverCount: SlotClass;
}

export const dataTableStyleDriver: DataTableStyleDriver = {
  root: "h-full grid grid-rows-[auto_1fr_auto] overflow-auto",
  toolbar: "flex items-center justify-between gap-2 px-6 py-2 border-b",
  toolbarLeft: "flex items-center gap-2",
  toolbarRight: "flex items-center gap-2",
  filterInput: "w-[240px]",
  tableContainer: "overflow-x-auto h-full overflow-auto relative",
  table: "w-full",
  thead: "bg-muted/40 sticky top-0 z-0",
  headerRow: "",
  headerCell: "px-6 py-2 text-left text-sm font-semibold text-muted-foreground",
  headerCellPinned: "sticky z-10 bg-muted/40",
  headerCellGroup: "bg-muted/60 text-xs font-semibold uppercase tracking-wider text-center border-b",
  headerCellSortable: "cursor-pointer select-none",
  headerCellContent: "flex items-center justify-between gap-2",
  filterButton: "p-1 rounded hover:bg-gray-200 transition-colors",
  filterButtonActive: "text-blue-600",
  tbody: "",
  bodyRow: "border-t hover:bg-accent/30",
  bodyRowSelected: "bg-accent/50",
  bodyCell: "px-6 py-2 text-sm align-middle",
  bodyCellPinned: "sticky z-10 bg-background",
  expandButton: "p-1 hover:bg-muted rounded",
  emptyState: "px-6 py-10 text-center text-muted-foreground",
  footer: "flex flex-col sm:flex-row sm:items-center sm:justify-between",
  footerText: "text-sm text-muted-foreground",
  // Filter popover styles
  filterPopoverHeader: "text-sm font-semibold border-b px-4 py-3",
  filterPopoverTitle: "text-sm font-semibold",
  filterPopoverSearchInput: "h-8 text-xs",
  filterPopoverSelectAll: "flex items-center space-x-2 px-7 py-2 border-b",
  filterPopoverSelectAllLabel: "text-sm font-semibold cursor-pointer",
  filterPopoverOptionsList: "max-h-60 overflow-y-auto space-y-1 px-4 py-3",
  filterPopoverOption: "flex items-center space-x-2 px-3 py-1.5 hover:bg-gray-50 rounded-md cursor-pointer",
  filterPopoverOptionLabel: "text-sm font-medium text-gray-700 cursor-pointer flex-1",
  filterPopoverEmpty: "text-xs text-gray-500 py-2 text-center",
  filterPopoverCount: "text-xs text-gray-500 pt-2 border-t px-4 py-2",
};

// Toast Component (Sonner)
export interface ToastStyleDriver {
  toaster: SlotClass;
  toast: SlotClass;
  toastTitle: SlotClass;
  toastDescription: SlotClass;
  toastActionButton: SlotClass;
  toastCancelButton: SlotClass;
  toastCloseButton: SlotClass;
  variants: Record<"default" | "success" | "error" | "warning" | "info", string>;
}

export const toastStyleDriver: ToastStyleDriver = {
  toaster: "toaster group",
  toast:
    "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-theme group-[.toaster]:rounded-theme-lg",
  toastTitle: "font-semibold text-sm",
  toastDescription: "text-sm text-muted-foreground",
  toastActionButton:
    "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground group-[.toast]:hover:bg-primary/90",
  toastCancelButton:
    "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground group-[.toast]:hover:bg-muted/80",
  toastCloseButton:
    "group-[.toast]:text-muted-foreground group-[.toast]:hover:text-foreground",
  variants: {
    default: "group-[.toaster]:border-border",
    success:
      "group-[.toaster]:bg-green-500/10 group-[.toaster]:border-green-500/30 group-[.toaster]:text-green-700 dark:group-[.toaster]:text-green-400",
    error:
      "group-[.toaster]:bg-destructive/10 group-[.toaster]:border-destructive/30 group-[.toaster]:text-destructive",
    warning:
      "group-[.toaster]:bg-amber-500/10 group-[.toaster]:border-amber-500/30 group-[.toaster]:text-amber-700 dark:group-[.toaster]:text-amber-400",
    info: "group-[.toaster]:bg-blue-500/10 group-[.toaster]:border-blue-500/30 group-[.toaster]:text-blue-700 dark:group-[.toaster]:text-blue-400",
  },
};

// Command Component (cmdk)
export interface CommandStyleDriver {
  root: SlotClass;
  dialog: SlotClass;
  dialogContent: SlotClass;
  input: SlotClass;
  inputWrapper: SlotClass;
  inputIcon: SlotClass;
  list: SlotClass;
  empty: SlotClass;
  group: SlotClass;
  groupHeading: SlotClass;
  separator: SlotClass;
  item: SlotClass;
  itemIcon: SlotClass;
  shortcut: SlotClass;
}

export const commandStyleDriver: CommandStyleDriver = {
  root: "flex h-full w-full flex-col overflow-hidden rounded-theme-lg bg-popover text-popover-foreground",
  dialog: "fixed inset-0 z-modal",
  dialogContent:
    "fixed left-1/2 top-1/2 z-modal w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-theme-xl border-theme bg-background shadow-theme-lg [&_[cmdk-group-heading]]:px-theme-3 [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group]:not([hidden])_~[cmdk-group]]:pt-0 [&_[cmdk-input-wrapper]_svg]:icon-sm [&_[cmdk-input]]:h-12 [&_[cmdk-item]]:px-theme-3 [&_[cmdk-item]]:py-theme-2",
  input:
    "flex h-10 w-full rounded-theme-md bg-transparent py-theme-3 text-body outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50",
  inputWrapper: "flex items-center border-b-theme px-theme-3",
  inputIcon: "icon-sm shrink-0 opacity-50",
  list: "max-h-72 overflow-y-auto overflow-x-hidden p-theme-2",
  empty: "py-theme-6 text-center text-body text-muted-foreground",
  group: "p-theme-2 [&_[cmdk-group-heading]]:px-theme-2 [&_[cmdk-group-heading]]:py-theme-2 [&_[cmdk-group-heading]]:text-caption [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground",
  groupHeading: "px-theme-2 py-theme-2 text-caption font-semibold text-muted-foreground",
  separator: "-mx-1 h-px bg-border",
  item: "relative flex cursor-default gap-theme-2 select-none items-center rounded-theme-md px-theme-3 py-theme-2 text-body outline-none data-[disabled=true]:pointer-events-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-[disabled=true]:opacity-50 [&_svg]:icon-sm [&_svg]:shrink-0",
  itemIcon: "icon-sm shrink-0",
  shortcut:
    "ml-auto text-caption tracking-widest text-muted-foreground",
};

// Combobox Component
export interface ComboboxStyleDriver {
  root: SlotClass;
  trigger: SlotClass;
  triggerIcon: SlotClass;
  content: SlotClass;
  search: SlotClass;
  list: SlotClass;
  empty: SlotClass;
  loading: SlotClass;
  group: SlotClass;
  groupLabel: SlotClass;
  item: SlotClass;
  itemCheck: SlotClass;
}

export const comboboxStyleDriver: ComboboxStyleDriver = {
  root: "relative",
  trigger:
    "flex h-9 w-full items-center justify-between gap-theme-2 rounded-theme-md border-theme bg-background px-theme-3 py-theme-2 text-body shadow-theme-sm transition-colors placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
  triggerIcon: "icon-sm shrink-0 opacity-50",
  content:
    "z-dropdown overflow-hidden rounded-theme-md border-theme bg-popover p-0 text-popover-foreground shadow-theme animate-in fade-in-0 zoom-in-95",
  search: "px-theme-3 py-theme-2 border-b-theme",
  list: "max-h-52 overflow-y-auto p-theme-2",
  empty: "py-theme-6 text-center text-body text-muted-foreground",
  loading: "py-theme-6 text-center text-body text-muted-foreground",
  group: "overflow-hidden p-theme-2",
  groupLabel:
    "px-theme-2 py-theme-2 text-caption font-semibold text-muted-foreground",
  item: "relative flex cursor-pointer select-none items-center gap-theme-2 rounded-theme-md px-theme-3 py-theme-2 text-body outline-none hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground [&_svg]:icon-sm [&_svg]:shrink-0",
  itemCheck: "icon-sm shrink-0",
};

// Pills / Tag Input Component
export interface PillsStyleDriver {
  root: SlotClass;
  container: SlotClass;
  tag: SlotClass;
  tagRemove: SlotClass;
  input: SlotClass;
}

export const pillsStyleDriver: PillsStyleDriver = {
  root: "flex flex-col gap-theme-2",
  container:
    "flex flex-wrap items-center gap-theme-2 rounded-theme-md border-theme bg-background px-theme-3 py-theme-2 focus-within:ring-2 focus-within:ring-ring min-h-9",
  tag: "inline-flex items-center gap-theme-1 rounded-theme-md bg-secondary px-theme-2 py-theme-1 text-caption font-medium text-secondary-foreground",
  tagRemove:
    "ml-1 rounded-theme-sm hover:bg-secondary-foreground/20 focus:outline-none focus:ring-1 focus:ring-ring",
  input:
    "flex-1 min-w-20 bg-transparent text-body outline-none placeholder:text-muted-foreground",
};

// Number Input Component
export interface NumberInputStyleDriver {
  root: SlotClass;
  container: SlotClass;
  input: SlotClass;
  button: SlotClass;
  buttonIcon: SlotClass;
}

export const numberInputStyleDriver: NumberInputStyleDriver = {
  root: "flex flex-col gap-theme-2",
  container: "relative flex items-center",
  input:
    "h-9 w-full rounded-theme-md border-theme bg-background px-theme-3 py-theme-2 text-body text-center tabular-nums shadow-theme-sm transition-colors placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
  button:
    "absolute h-7 w-7 rounded-theme-sm flex items-center justify-center text-muted-foreground hover:bg-accent hover:text-accent-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-50",
  buttonIcon: "icon-sm",
};

// Toggle Component
export interface ToggleStyleDriver {
  root: SlotClass;
  variants: Record<string, SlotClass>;
  sizes: Record<string, SlotClass>;
}

export const toggleStyleDriver: ToggleStyleDriver = {
  root: "inline-flex items-center justify-center gap-theme-2 rounded-theme-md font-medium transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground",
  variants: {
    default: "bg-transparent",
    outline:
      "border-theme bg-transparent shadow-theme-sm hover:bg-accent hover:text-accent-foreground",
  },
  sizes: {
    default: "h-9 px-theme-3 min-w-9",
    sm: "h-8 px-theme-2 min-w-8",
    lg: "h-10 px-theme-4 min-w-10",
  },
};

// Toggle Group Component
export interface ToggleGroupStyleDriver {
  root: SlotClass;
  item: SlotClass;
}

export const toggleGroupStyleDriver: ToggleGroupStyleDriver = {
  root: "inline-flex items-center justify-center gap-theme-1 rounded-theme-md bg-muted p-theme-1",
  item: "inline-flex items-center justify-center rounded-theme-sm px-theme-3 py-theme-2 text-body font-medium transition-colors hover:bg-muted-foreground/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-theme-sm",
};

// Stepper Component
export interface StepperStyleDriver {
  root: SlotClass;
  rootVertical: SlotClass;
  step: SlotClass;
  stepVertical: SlotClass;
  indicator: SlotClass;
  indicatorActive: SlotClass;
  indicatorCompleted: SlotClass;
  indicatorError: SlotClass;
  connector: SlotClass;
  connectorVertical: SlotClass;
  connectorActive: SlotClass;
  content: SlotClass;
  title: SlotClass;
  description: SlotClass;
}

export const stepperStyleDriver: StepperStyleDriver = {
  root: "flex items-center gap-theme-2",
  rootVertical: "flex flex-col gap-theme-2",
  step: "flex items-center gap-theme-3",
  stepVertical: "flex gap-theme-3",
  indicator:
    "flex h-8 w-8 shrink-0 items-center justify-center rounded-theme-full border-2 border-muted text-body font-medium text-muted-foreground transition-colors",
  indicatorActive: "border-primary bg-primary text-primary-foreground",
  indicatorCompleted: "border-primary bg-primary text-primary-foreground",
  indicatorError: "border-destructive bg-destructive text-destructive-foreground",
  connector: "h-0.5 flex-1 bg-muted transition-colors",
  connectorVertical: "ml-4 w-0.5 flex-1 bg-muted transition-colors min-h-8",
  connectorActive: "bg-primary",
  content: "flex flex-col",
  title: "text-body font-medium text-foreground",
  description: "text-caption text-muted-foreground",
};

// Tree Menu Component
export interface TreeMenuStyleDriver {
  root: SlotClass;
  item: SlotClass;
  itemActive: SlotClass;
  itemDisabled: SlotClass;
  itemContent: SlotClass;
  expandIcon: SlotClass;
  icon: SlotClass;
  label: SlotClass;
  children: SlotClass;
}

export const treeMenuStyleDriver: TreeMenuStyleDriver = {
  root: "flex flex-col gap-theme-1",
  item: "flex flex-col",
  itemActive: "bg-accent text-accent-foreground",
  itemDisabled: "pointer-events-none opacity-50",
  itemContent:
    "flex items-center gap-theme-2 rounded-theme-md px-theme-3 py-theme-2 text-body cursor-pointer hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors",
  expandIcon: "icon-sm shrink-0 text-muted-foreground transition-transform",
  icon: "icon-sm shrink-0",
  label: "flex-1 truncate",
  children: "flex flex-col gap-theme-1 pl-theme-4",
};

// Side Panel Component
export interface SidePanelStyleDriver {
  overlay: SlotClass;
  content: SlotClass;
  contentLeft: SlotClass;
  contentRight: SlotClass;
  header: SlotClass;
  title: SlotClass;
  description: SlotClass;
  close: SlotClass;
  body: SlotClass;
  footer: SlotClass;
  sizes: Record<string, SlotClass>;
}

export const sidePanelStyleDriver: SidePanelStyleDriver = {
  overlay:
    "fixed inset-0 z-modal bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  content:
    "fixed z-modal flex h-full flex-col gap-theme-4 bg-background shadow-theme-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:duration-300",
  contentLeft:
    "inset-y-0 left-0 border-r-theme data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
  contentRight:
    "inset-y-0 right-0 border-l-theme data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
  header: "flex flex-col gap-theme-2 px-theme-6 pt-theme-6",
  title: "text-h5 font-semibold text-foreground",
  description: "text-body text-muted-foreground",
  close:
    "absolute right-4 top-4 rounded-theme-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring disabled:pointer-events-none",
  body: "flex-1 overflow-auto px-theme-6 py-theme-4",
  footer:
    "flex items-center justify-end gap-theme-3 border-t-theme px-theme-6 py-theme-4",
  sizes: {
    sm: "w-80",
    default: "w-96",
    lg: "w-[480px]",
    xl: "w-[600px]",
    full: "w-screen",
  },
};

export const styleDrivers = {
  card: cardStyleDrivers,
  label: labelStyleDriver,
  pageHeader: pageHeaderStyleDriver,
  sidebar: sidebarStyleDriver,
  sheet: sheetStyleDriver,
  modal: modalStyleDriver,
  tabs: tabsStyleDriver,
  horizontalTabs: horizontalTabsStyleDriver,
  verticalTabs: verticalTabsStyleDriver,
  button: buttonStyleDriver,
  input: inputStyleDriver,
  select: selectStyleDriver,
  alert: alertStyleDriver,
  badge: badgeStyleDriver,
  filterBuilder: filterBuilderStyleDriver,
  accordion: accordionStyleDriver,
  listing: listingStyleDriver,
  dropdownMenu: dropdownMenuStyleDriver,
  pagination: paginationStyleDriver,
  progress: progressStyleDriver,
  tooltip: tooltipStyleDriver,
  scaffold: scaffoldStyleDriver,
  pageContainer: pageContainerStyleDriver,
  section: sectionStyleDriver,
  avatar: avatarStyleDriver,
  calendar: calendarStyleDriver,
  popover: popoverStyleDriver,
  selectUI: selectUIStyleDriver,
  skeleton: skeletonStyleDriver,
  emptyState: emptyStateStyleDriver,
  searchBox: searchBoxStyleDriver,
  spinner: spinnerStyleDriver,
  loadingState: loadingStateStyleDriver,
  errorState: errorStateStyleDriver,
  formField: formFieldStyleDriver,
  formSection: formSectionStyleDriver,
  checkbox: checkboxStyleDriver,
  radioGroup: radioGroupStyleDriver,
  agGrid: agGridStyleDriver,
  dataTable: dataTableStyleDriver,
  toast: toastStyleDriver,
  command: commandStyleDriver,
  combobox: comboboxStyleDriver,
  pills: pillsStyleDriver,
  numberInput: numberInputStyleDriver,
  toggle: toggleStyleDriver,
  toggleGroup: toggleGroupStyleDriver,
  stepper: stepperStyleDriver,
  treeMenu: treeMenuStyleDriver,
  sidePanel: sidePanelStyleDriver,
};
